import { useMemo, useState } from "react";

import clsx from "clsx";

import {
  type FetchMediaPlanData_me_mmm_mediaPlanData_mediaPlanList,
  type FetchMediaPlanData_me_mmm_mediaPlanData_mediaPlanList_kpis,
} from "@north-beam/nb-client-side/nb-api-graphql-generated";
import { VERIFY_PERFORMANCE_GOALS } from "@north-beam/nb-common";
import { IconButton, PrimaryButton, TertiaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";
import { TextInput } from "@shared/text-input";

import { useUpdateMediaPlanDraftStep } from "../../hooks/use-update-media-plan-draft-step";
import { DEPRECATED_WIZARD_STEPS as WIZARD_STEPS } from "../consts";

import { MediaPlanVerifyButton } from "./media-plan-verify-button";

interface Props {
  currentIndex: number;
  kpis: FetchMediaPlanData_me_mmm_mediaPlanData_mediaPlanList_kpis[];
  selectedMediaPlan: FetchMediaPlanData_me_mmm_mediaPlanData_mediaPlanList;
}

export const PerformanceGoalStep = ({
  currentIndex,
  kpis,
  selectedMediaPlan,
}: Props) => {
  const {
    verify,
    unverify,
    addPendingData,
    removePendingData,
    isUpdateMediaPlanDraftStepLoading,
  } = useUpdateMediaPlanDraftStep({
    draftStep: VERIFY_PERFORMANCE_GOALS,
    selectedMediaPlan,
  });

  const isDraft = selectedMediaPlan.state === "draft";

  const [showPendingDataModal, setShowPendingDataModal] = useState(false);
  const [pendingValue, setPendingValue] = useState("");
  const closeModal = () => {
    setPendingValue("");
    setShowPendingDataModal(false);
  };

  const isVerified =
    selectedMediaPlan.step_tracker[VERIFY_PERFORMANCE_GOALS].verified;
  const pendingData = useMemo(
    () => selectedMediaPlan.step_tracker[VERIFY_PERFORMANCE_GOALS].pending_data,
    [selectedMediaPlan],
  );

  const _verify = async () => verify(kpis);
  const _addPendingData = async (newPendingData: string) => {
    await addPendingData(newPendingData);
    closeModal();
  };

  return (
    <div>
      <div className="mb-4">
        <div className="text-lg font-semibold">
          {WIZARD_STEPS[currentIndex].title}
        </div>
        {WIZARD_STEPS[currentIndex].description && isDraft && (
          <div className="text-sm text-interstellar-1">
            {WIZARD_STEPS[currentIndex].description}
          </div>
        )}
      </div>
      <div
        className={clsx("overflow-y-auto", {
          "max-h-[360px]": pendingData.length === 0 && isDraft,
          "max-h-[420px]": pendingData.length === 0 && !isDraft,
          "max-h-[180px]": pendingData.length > 0,
        })}
      >
        {kpis.length > 0 ? (
          kpis.map((kpi) => (
            <div
              key={kpi.id}
              className="py-2 text-xs flex items-center gap-x-1.5 border-b border-b-[#E5E5E5]"
            >
              {kpi.friendly_name}
            </div>
          ))
        ) : (
          <div className="text-xs text-interstellar-1">
            No performance goals added
          </div>
        )}
      </div>
      {pendingData.length > 0 && (
        <>
          <div className="text-xs font-bold border-b border-b-[#E5E5E5] pt-4 pb-1">
            PENDING GOALS
          </div>
          <div className="overflow-y-auto max-h-[160px]">
            {pendingData.map((pendingDatum, index) => (
              <div
                key={pendingDatum}
                className="py-2 text-xs flex items-center justify-between gap-x-1.5 border-b border-b-[#E5E5E5]"
              >
                {pendingDatum}
                <IconButton
                  variant="tertiary"
                  onClick={() => removePendingData(index)}
                  loading={isUpdateMediaPlanDraftStepLoading}
                >
                  <i className="fa-regular fa-trash-can text-danger" />
                </IconButton>
              </div>
            ))}
          </div>
        </>
      )}
      <MediaPlanVerifyButton
        className="mt-4"
        verify={_verify}
        unverify={unverify}
        isVerified={isVerified}
        isLoading={isUpdateMediaPlanDraftStepLoading}
      >
        <IconButton
          variant="tertiary"
          className="px-2"
          leftIcon={<i className="fa-regular fa-plus" />}
          onClick={() => setShowPendingDataModal(true)}
        >
          ADD NEW GOAL
        </IconButton>
      </MediaPlanVerifyButton>
      <Modal
        isOpen={showPendingDataModal}
        onClose={closeModal}
        titleContent={<div className="font-bold text-xl">Add New Goal</div>}
      >
        <TextInput
          label="Goal Name"
          isRequired
          placeholder="Goal Name"
          value={pendingValue}
          onChange={(e) => setPendingValue(e.target.value)}
        />
        <div className="mt-4 flex items-center justify-end">
          <TertiaryButton
            loading={isUpdateMediaPlanDraftStepLoading}
            onClick={closeModal}
          >
            CANCEL
          </TertiaryButton>
          <PrimaryButton
            loading={isUpdateMediaPlanDraftStepLoading}
            onClick={async () => {
              await _addPendingData(pendingValue);
              setShowPendingDataModal(false);
            }}
          >
            ADD GOAL
          </PrimaryButton>
        </div>
      </Modal>
    </div>
  );
};
